import React, { useState } from 'react';
import { TextField } from "../TextField";
import { DateTimePicker } from "../DateTimePicker";
import './Grid.scss';
import { Select } from "../Select";
import { TextInput } from "../TextInput";
import { PLEASE_SELECT } from "../../../shared/util/constant";
import { Icon } from "../Icon";
import { IconType } from "../Icon/interfaces";
import { Button } from 'primereact/button';

export class GridDefaultCellTemplates {

    private static defaultErrorHanlder = cell => {
        if (cell?.row?.original?.editingRow?.errors && Object.keys(cell?.row?.original?.editingRow?.errors).length > 0) {
            let colError = cell.row.original.editingRow.errors[cell.column.fieldName];
            if (colError) {
                // inputProps is required for select edit templates as tooltip are shown on div in select component. 
                return { isInvalid: true, title: colError.warningText, inputProps: { title: colError.warningText } };
            }
        }

        return {};
    }

    private static defaultCellViewTemplate = cell => <div><span title={cell.value}>{cell.value}</span></div>;

    static EditableGridColumn = {
        CellTemplate: cell => {
            if (cell?.row?.original?.enableGridRowEditing == true
                && cell.column.CellEditTemplate) {
                return cell.column.CellEditTemplate(cell);
            }

            if (cell.column.CellViewTemplate) {
                return cell.column.CellViewTemplate(cell);
            }

            return this.defaultCellViewTemplate(cell);
        }
    }

    static SelectFieldGridColumn = {
        CellTemplate: cell => {
            let cellTemplateParameters = cell.column.CellEditTemplateParameters ? cell.column.CellEditTemplateParameters : {};
            let cellDynamicParameters = {};
            if (cell.column.CellEditTemplateErrorHandler) {
                cellDynamicParameters = cell.column.CellEditTemplateErrorHandler(cell);
            }

            // Do edting on editing row (copy of original row) only.
            let editingRow: any = {};
            if (cell.row?.original?.editingRow) {
                editingRow = cell.row.original.editingRow;
            }
            // Destructure only valid Select props from cellTemplateParameters; exclude handler/internal keys
            const { options, sort, cellDataChangeHandler, cellClickHandler, ...selectProps } = cellTemplateParameters;
            const [selectedValue, setSelectedValue] = useState(editingRow[cell.column.fieldName]);
            return <Select
                id={`selectGridRow${editingRow.id}${cell?.column?.id}`}
                key={`selectGridRow${editingRow.id}${cell?.column?.id}`}
                placeholder={PLEASE_SELECT}
                label={PLEASE_SELECT}
                sortOptions={sort}
                options={options || []}
                showAsFloatingLabel={false}
                defaultSelected={options?.filter(({ value }) => value === selectedValue)}
                onChange={
                    (values) => {
                        if (values.length > 0) {
                            setSelectedValue(values[0]);
                            editingRow[cell.column.fieldName] = values[0];
                        }
                        else {
                            setSelectedValue(null);
                            editingRow[cell.column.fieldName] = null;
                        }
                    }
                }
                {...selectProps}
                {...cellDynamicParameters} />
        },
        ErrorHandler: this.defaultErrorHanlder
    }

    static DateFieldGridColumn = {
        CellTemplate: cell => {
            let cellTemplateParameters = cell.column.CellEditTemplateParameters ? cell.column.CellEditTemplateParameters : {};
            let cellDynamicParameters = {};
            if (cell.column.CellEditTemplateErrorHandler) {
                cellDynamicParameters = cell.column.CellEditTemplateErrorHandler(cell);
            }

            // Do edting on editing row (copy of original row) only.
            let editingRow: any = {};
            if (cell.row?.original?.editingRow) {
                editingRow = cell.row.original.editingRow;
            }

            return <div className="grd-datetime-picker-div">
                <DateTimePicker
                    id={`gridDateTimePicker${editingRow.id}${cell?.column?.id}`}
                    key={`gridDateTimePicker${editingRow.id}${cell?.column?.id}`}
                    dateFormatCalendar="LLLL yyyy"
                    dropdownMode="scroll"
                    selected={editingRow[cell.column.fieldName]}
                    popperClassName="grd-date-time-picker-popper"
                    onChange={(date) => {
                        if (cellTemplateParameters.cellDataChangeHandler) {
                            cellTemplateParameters.cellDataChangeHandler(editingRow, cell.column, date, null)
                        }
                    }}
                    {...cellTemplateParameters}
                    {...cellDynamicParameters} />
            </div>
        },
        ErrorHandler: this.defaultErrorHanlder
    }

    static TextInputGridColumn = {
        CellTemplate: cell => {
            let cellTemplateParameters = cell.column.CellEditTemplateParameters ? cell.column.CellEditTemplateParameters : {};
            let cellDynamicParameters = {};
            if (cell.column.CellEditTemplateErrorHandler) {
                cellDynamicParameters = cell.column.CellEditTemplateErrorHandler(cell);
            }

            // Do edting on editing row (copy of original row) only.
            let editingRow: any = {};
            if (cell.row?.original?.editingRow) {
                editingRow = cell.row.original.editingRow;
            }

            const { cellDataChangeHandler, cellClickHandler, type, maxLength, allowDecimal, ...inputProps } = cellTemplateParameters;
            const [localValue, setLocalValue] = useState(editingRow[cell.column.fieldName] ?? '');
            return <TextInput
                value={localValue}
                maxLength={maxLength}
                onKeyDown={(e) => {
                    if (type === 'number') {
                        const allowedKeys = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
                        const pattern = allowDecimal === false ? /[\d]/ : /[\d.]/;
                        if (!allowedKeys.includes(e.key) && !pattern.test(e.key)) {
                            e.preventDefault();
                        }
                        // Enforce maxLength for numeric fields (HTML maxLength doesn't work on type=number)
                        if (maxLength && !allowedKeys.includes(e.key) && localValue.length >= maxLength) {
                            e.preventDefault();
                        }
                    }
                }}
                onChange={(event) => {
                    const newVal = event.currentTarget.value;
                    if (maxLength && newVal.length > maxLength) return;
                    setLocalValue(newVal);
                    editingRow[cell.column.fieldName] = newVal;
                }}

                {...inputProps}
                {...cellDynamicParameters}>
            </TextInput>;
        },
        ErrorHandler: this.defaultErrorHanlder
    }

    static TextFieldGridColumn = {
        CellTemplate: cell => {
            let cellTemplateParameters = cell.column.CellEditTemplateParameters ? cell.column.CellEditTemplateParameters : {};
            let cellDynamicParameters = {};
            if (cell.column.CellEditTemplateErrorHandler) {
                cellDynamicParameters = cell.column.CellEditTemplateErrorHandler(cell);
            }

            // Do edting on editing row (copy of original row) only.
            let editingRow: any = {};
            if (cell.row?.original?.editingRow) {
                editingRow = cell.row.original.editingRow;
            }

            return <TextField
                value={editingRow[cell.column.fieldName]}
                onChange={(event) => {
                    if (cellTemplateParameters.cellDataChangeHandler) {
                        cellTemplateParameters.cellDataChangeHandler(editingRow, cell.column, event.currentTarget.value, null)
                    }
                }}

                {...cellTemplateParameters}
                {...cellDynamicParameters}>
            </TextField>;
        },
        ErrorHandler: this.defaultErrorHanlder
    }

    static SaveRowGridColumn = {
        CellTemplate: (cell: any) => {
            const [loading, setLoading] = React.useState(false);

            const handleSave = async () => {
                if (!loading && cell.column.CellEditTemplateParameters?.cellClickHandler) {
                    setLoading(true);
                    try {
                        await cell.column.CellEditTemplateParameters?.cellClickHandler(
                            cell.row.original,
                            cell.column,
                            'save_row'
                        );
                    } finally {
                        setLoading(false);
                    }
                }
            };

            return (
                <div className="grd-center-content grd-center-content--edit-action d-flex gap-2">
                    <Button
                        icon="bi bi-floppy"
                        loading={loading}
                        disabled={loading}
                        onClick={handleSave}
                        severity="info"
                        size="small"
                        tooltip="Save"
                        // label='Save'
                        className='text-nowrap align-self-center customerTasks-buttons'
                    />
                    <Button
                        icon="bi bi-x"
                        onClick={() => {
                            if (!loading && cell.column.CellEditTemplateParameters?.cellClickHandler) {
                                cell.column.CellEditTemplateParameters?.cellClickHandler(
                                    cell.row.original,
                                    cell.column,
                                    'cancel_row_editing'
                                );
                            }
                        }}
                        disabled={loading}
                        outlined={true}
                        size="small"
                        tooltip="Cancel"
                        // label='Cancel'
                        className='text-nowrap align-self-center customerTasks-buttons'

                    />
                </div>
            );
        }
    }

    static EditRowGridColumn = {
        CellTemplate: (cell: any) => {
            let disableButton = false;
            if (cell?.row?.original?.isGridInEditMode) {
                disableButton = true;
            }

            let templateToRender = <div className="grd-center-content">
                <Icon icon={'edit' as IconType}
                    className="grd-cell-icon grd-cell-icon--edit"
                    title="Edit"
                    onClick={() => {
                        if (cell.column.CellEditTemplateParameters?.cellClickHandler && !disableButton) {
                            cell.column.CellEditTemplateParameters?.cellClickHandler(cell.row.original, cell.column, 'edit_row');
                        }
                    }} />
            </div>;

            return templateToRender;
        },
    }
}